import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { POLL_INTERVAL_MS, coverageLabel, type DashboardSummary, type SystemHealth, type TenantAlert } from '@ww/shared';
import { I, Icon, KIND_ICON } from '../lib/icons';
import { affectedSummary, kindStyle, watchAreaLabel } from '../lib/status';
import { fmtInterval, fmtTime, msSince, useNow } from '../lib/time';

/* ---------- banners ---------- */

const bannerBase: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '10px 14px 10px 18px',
  borderRadius: 10,
  flexShrink: 0,
};

export function ActiveBanner({ dashboard, large = false }: { dashboard: DashboardSummary | null; large?: boolean }) {
  const navigate = useNavigate();
  const n = dashboard?.counts?.warning ?? 0;
  if (!dashboard || n <= 0) return null;
  const tz = dashboard.tenant?.timezone;
  const top = (dashboard.alerts ?? []).find((a) => (a.evaluation?.category ?? a.alert?.category) === 'warning');
  const inside = (top?.evaluation?.affectedAssets ?? []).filter((a) => a.relation === 'inside').map((a) => a.name);
  const where = coverageLabel(dashboard.tenant);
  const sub = top
    ? `${top.alert.event} until ${fmtTime(top.alert.expires ?? top.alert.ends, tz)}` +
      (inside.length ? `: ${inside.join(', ')} inside the polygon` : '')
    : '';
  return (
    <div role="alert" style={{ ...bannerBase, background: '#6a1720', border: '1px solid #a3313b' }}>
      <Icon d={I.alert} size={large ? 38 : 32} strokeWidth={2} style={{ color: '#fff' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1, minWidth: 0 }}>
        <div
          className="cond"
          style={{ fontSize: large ? 28 : 23, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: '#fff' }}
        >
          Active severe weather · {n} warning{n === 1 ? '' : 's'} in effect for the {where} area
        </div>
        {sub && <div style={{ fontSize: large ? 17 : 15, color: '#ffdfe2' }}>{sub}</div>}
      </div>
      <button
        type="button"
        className="hov"
        onClick={() => navigate('/alerts')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 44,
          padding: '0 16px',
          borderRadius: 8,
          border: '1px solid #f3a0a6',
          background: '#8f222c',
          color: '#fff',
          fontSize: 15,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        <span>View alerts</span>
        <Icon d={I.chevR} size={16} />
      </button>
    </div>
  );
}

export function StaleBanner({ health, tz, large = false }: { health: SystemHealth | null; tz?: string | null; large?: boolean }) {
  const navigate = useNavigate();
  const now = useNow(1000);
  if (!health?.stale) return null;
  const last = health.polling?.lastPollSuccessAt ?? null;
  const since = msSince(last, now);
  const mins = since === null ? null : Math.floor(since / 60_000);
  return (
    <div role="alert" style={{ ...bannerBase, background: '#4a3410', border: '1px solid #9a6d22' }}>
      <Icon d={I.refresh} size={large ? 38 : 32} strokeWidth={2} style={{ color: '#ffd08a' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1, minWidth: 0 }}>
        <div
          className="cond"
          style={{ fontSize: large ? 28 : 23, fontWeight: 700, letterSpacing: '.03em', textTransform: 'uppercase', color: '#ffe2b8' }}
        >
          NWS alert feed is stale · {mins === null ? 'unknown' : `${mins} min`}
        </div>
        <div style={{ fontSize: large ? 17 : 15, color: '#ffe9cc' }}>
          Last successful poll {last ? fmtTime(last, tz) : 'unknown'}. Showing last known alerts; asset evaluation continues on
          cached data.
        </div>
      </div>
      <button
        type="button"
        className="hov"
        onClick={() => navigate('/health')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 44,
          padding: '0 16px',
          borderRadius: 8,
          border: '1px solid #d9a24c',
          background: '#6b4a15',
          color: '#fff',
          fontSize: 15,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        <span>System health</span>
        <Icon d={I.chevR} size={16} />
      </button>
    </div>
  );
}

/* ---------- alert feed ---------- */

export function AlertCard({
  ta,
  tz,
  stale,
  large = false,
  onOpen,
}: {
  ta: TenantAlert;
  tz?: string | null;
  stale?: string | null;
  large?: boolean;
  onOpen: () => void;
}) {
  const a = ta.alert;
  const k = kindStyle(a.event);
  const hasAssets = (ta.evaluation?.affectedAssets ?? []).length > 0;
  return (
    <button
      type="button"
      className="hov"
      onClick={onOpen}
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        textAlign: 'left',
        width: '100%',
        padding: '11px 12px',
        borderRadius: 8,
        flexShrink: 0,
        background: '#111d33',
        border: '1px solid #1e2c46',
        borderLeft: `3px solid ${k.fill}`,
      }}
    >
      <span
        style={{
          width: large ? 48 : 40,
          height: large ? 48 : 40,
          flexShrink: 0,
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: k.tint,
        }}
      >
        <Icon d={KIND_ICON[k.kind]} size={large ? 26 : 22} style={{ color: k.color }} />
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flexGrow: 1 }}>
        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontWeight: 600, fontSize: large ? 19 : 15.5, color: k.color }}>{a.event}</span>
          <span className="mono" style={{ fontSize: large ? 14 : 12, color: '#9fb0c8', flexShrink: 0 }}>
            Issued {fmtTime(a.sent, tz)}
          </span>
        </span>
        <span
          style={{
            fontSize: large ? 15.5 : 13.5,
            color: '#d3ddeb',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {a.areaDesc || '—'}
        </span>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: large ? 14.5 : 12.5, color: '#9fb0c8' }}>
          <span>
            Until {fmtTime(a.expires ?? a.ends, tz)}
            {watchAreaLabel(ta.evaluation)
              ? ` · via ${watchAreaLabel(ta.evaluation)}`
              : ta.evaluation?.precision === 'zone'
                ? ' · County match'
                : ''}
          </span>
          {stale && <span className="tag tag-stale">{stale}</span>}
        </span>
        <span style={{ fontSize: large ? 14.5 : 12.5, color: hasAssets ? '#ffc9a0' : '#8193ad' }}>
          {affectedSummary(ta.evaluation)}
        </span>
      </span>
    </button>
  );
}

export function AlertFeed({
  dashboard,
  health,
  large = false,
}: {
  dashboard: DashboardSummary | null;
  health: SystemHealth | null;
  large?: boolean;
}) {
  const navigate = useNavigate();
  const now = useNow(5000);
  const alerts = dashboard?.alerts ?? [];
  const tz = dashboard?.tenant?.timezone;
  const county = coverageLabel(dashboard?.tenant);
  const since = msSince(health?.polling?.lastPollSuccessAt, now);
  const staleTag = health?.stale && since !== null ? `STALE ${Math.floor(since / 60_000)} MIN` : null;
  const interval = health?.polling?.intervalMs ?? POLL_INTERVAL_MS.normal;
  return (
    <section className="panel" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid #1e2c46' }}>
        <Icon d={I.bell} size={large ? 24 : 20} style={{ color: '#ff7a7f' }} />
        <h2 style={{ margin: 0, fontSize: large ? 21 : 17, fontWeight: 600, flexGrow: 1 }}>Active alerts</h2>
        <span className="mono" style={{ fontSize: 13, color: '#9fb0c8' }}>
          {dashboard ? `${alerts.length} for tenant` : 'Loading…'}
        </span>
      </div>
      <div className="scroll" style={{ flexGrow: 1, minHeight: 0, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {dashboard && alerts.length === 0 && (
          <div
            style={{
              margin: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              padding: 24,
              textAlign: 'center',
            }}
          >
            <Icon d={I.check} size={40} style={{ color: '#3fb950' }} />
            <div style={{ fontSize: 18, fontWeight: 600 }}>No active alerts</div>
            <div style={{ fontSize: 14, color: '#9fb0c8', lineHeight: 1.45, maxWidth: 260 }}>
              Nothing from NWS affects {county} or its monitored assets. Polling every {fmtInterval(interval)}.
            </div>
          </div>
        )}
        {alerts.map((ta) => (
          <AlertCard
            key={ta.alert.nwsId}
            ta={ta}
            tz={tz}
            stale={staleTag}
            large={large}
            onOpen={() => navigate(`/alerts/${encodeURIComponent(ta.alert.nwsId)}`)}
          />
        ))}
      </div>
    </section>
  );
}

/* ---------- status tiles ---------- */

export function StatusTiles({ dashboard, large = false }: { dashboard: DashboardSummary | null; large?: boolean }) {
  const navigate = useNavigate();
  const c = dashboard?.counts;
  const warnings = c?.warning ?? 0;
  const watches = c?.watch ?? 0;
  const advisories = (c?.advisory ?? 0) + (c?.statement ?? 0);
  const affected = dashboard?.affectedAssetCount ?? 0;
  const enabled = dashboard?.enabledAssetCount ?? 0;
  const total = dashboard?.assetCount ?? 0;
  const tileCss = (n: number, bg: string, bd: string): CSSProperties =>
    n ? { background: bg, border: `1px solid ${bd}` } : { background: '#0f1a2d', border: '1px solid #1e2c46' };
  const v = (n: number) => (dashboard ? String(n) : '–');
  const tiles = [
    { value: v(warnings), label: 'Warnings', icon: I.tornado, color: '#ff7a7f', css: tileCss(warnings, '#33131a', '#6e2a31'), to: '/alerts?filter=warning' },
    { value: v(watches), label: 'Watches', icon: I.watch, color: '#f7cf4f', css: tileCss(watches, '#2e2610', '#65561f'), to: '/alerts?filter=watch' },
    { value: v(advisories), label: 'Advisories & statements', icon: I.flood, color: '#4fd6ef', css: tileCss(advisories, '#08262e', '#1a5062'), to: '/alerts?filter=advisory' },
    {
      value: dashboard ? `${affected}/${enabled}` : '–',
      label: 'Assets in or near an alert',
      icon: I.alert,
      color: affected ? '#ffa857' : '#78b7ff',
      css: tileCss(affected, '#2d1d10', '#6b4520'),
      to: '/assets',
    },
    { value: v(enabled), label: `Monitored assets${dashboard ? ` (of ${total})` : ''}`, icon: I.pin, color: '#78b7ff', css: tileCss(0, '', ''), to: '/assets' },
  ];
  return (
    <div className="tiles">
      {tiles.map((t) => (
        <button
          key={t.label}
          type="button"
          className="hov"
          onClick={() => navigate(t.to)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            height: large ? 96 : 80,
            padding: '0 16px',
            borderRadius: 10,
            textAlign: 'left',
            ...t.css,
          }}
        >
          <Icon d={t.icon} size={large ? 36 : 30} style={{ color: t.color }} />
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <span className="cond" style={{ fontSize: large ? 44 : 36, fontWeight: 700, lineHeight: 1 }}>
              {t.value}
            </span>
            <span style={{ fontSize: large ? 15.5 : 13.5, color: '#c2cedf' }}>{t.label}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
